import { useEffect, useRef, useState } from "react";

import styled, { css, keyframes } from "styled-components";

import "./PDFChatInputStyle.css";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import GeneralText from "components/atoms/text/GeneralText";
import StyledText from "components/atoms/text/StyledText";

import Constants from "utils/constants";

const ThreadContainer = styled.div`
    padding: 16px 16px;
    width: 100%;
    height: auto;
    background-color: ${({ bgColor }) => (bgColor ? bgColor : "var(--color-White)")};
    border-bottom: dashed 1px var(--color-Line);
    transition: background-color 80ms ease-in;

    cursor: pointer;
    &:hover {
        background-color: var(--color-ListHover);
    }
`;

const ThreadText = styled(StyledText)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    text-align: justify;

    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    margin-bottom: 8px;
`;

const ThreadFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ThreadDateText = styled(StyledText)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const ThreadEnterText = styled(StyledText)`
    font-size: 1.2rem;
    color: var(--color-Grey1);
    transition: all 120ms ease-in;

    &:hover {
        cursor: pointer;
        color: var(--color-ButtonHover1);
    }
`;

// 깜빡임 애니메이션 정의
const blinkBg = keyframes`
    0% { background-color: var(--color-Base2); }
    50% { background-color: var(--color-White); }
    100% { background-color: var(--color-Base2); }
`;

const MessageContainer = styled.div`
    padding: 20px 16px;
    width: 100%;
    height: auto;
    background-color: ${({ bgColor }) => (bgColor ? bgColor : "var(--color-White)")};
    border-bottom: dashed 1px var(--color-Line);
    transition: background-color 80ms ease-in;

    ${({ isBlinking }) =>
        isBlinking &&
        css`
            animation: ${blinkBg} 1s linear 0s 2 alternate;
            /* 1.0s * 2 = 2초 */
        `}
`;

const OptionBtnWrap = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: ${({ active }) => (active ? "flex" : "none")};
    align-items: center;
    padding: 2px;
    height: 30px;
    background-color: var(--color-White);
    border-radius: 8px;
    border: solid 1px var(--color-Outline);
    box-shadow: 2px 0px 4px rgba(0, 0, 0, 0.05);
`;

const SourceContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
`;

const DivideLine = styled.div`
    display: block;
    margin: 8px 0 0 0;
    width: 100%;
    height: 1px;
    background-color: var(--color-Line);
`;

const SourceSubText = styled.div`
    font-size: 10px;
    font-weight: 400;
    border-radius: 4px;
    background-color: var(--color-InfoBox);
    color: var(--color-Grey1);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
        cursor: pointer;
        background-color: var(--color-Base1);
    }
`;

const SourceNumText = styled.span`
    margin: 0 4px 0 4px;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const DeletedView = styled.div`
    margin: 4px 0 0 0;
    padding: 6px;
    width: 100%;
    background-color: var(--color-InfoBox);
    border-radius: 4px;
`;

const MessageSection = styled.div``;

const MessageHeader = styled.div`
    display: flex;
    margin-bottom: 16px;
`;

const ProfileIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 8px 0 0;
    min-width: 24px;
    width: 24px;
    height: 24px;
    background-color: var(--color-Base1);
    border-radius: 12px;
`;

const ProfileText = styled(StyledText)`
    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
    background-color: var(--color-Background);

    padding: 0 4px;
    border-radius: 4px;
`;

const MessageDateText = styled(StyledText)`
    margin-left: auto;

    display: flex;
    align-items: center;

    font-size: 1.2rem;
    color: var(--color-Grey1);
`;

const MessageBody = styled.div``;

const HighlightImage = styled.div`
    position: relative;
    float: left;
    margin-right: 8px;

    width: 48px;
    height: 48px;
    border: 1px solid
        ${({ color }) => {
            return color;
        }};

    overflow: hidden;
`;

const HightlightPageText = styled.div`
    position: absolute;
    inset: 0;

    display: flex;
    justify-content: center;
    align-items: center;

    border-left: 10px solid
        ${({ color }) => {
            return color;
        }};

    opacity: 1;
    color: var(--color-White);
    background-color: var(--color-Black);

    &:hover {
        cursor: pointer;

        opacity: 0.5;
        color: transparent;
        background-color: transparent;
    }
`;

const MessageText = styled(StyledText)`
    font-size: 1.4rem;
    color: var(--color-SubBlack);
    white-space: pre-line;
    text-align: justify;
`;

const NoMessageContainer = styled.div`
    padding: 16px;
    color: #888;
`;

function PDFChatMessage(props) {
    // 스레드 호버시 나타나는 버튼
    const [showMessageOption, setShowMessageOption] = useState(false);

    const parseDate = (datetime) => {
        return datetime.split(" ")[0].replaceAll("-", ".");
    };
    const parseTime = (datetime) => {
        return datetime.split(" ")[1].substring(0, "HH:mm".length);
    };

    // 포커스 메시지 처리
    const itemRef = useRef();

    // 깜빡임 상태 관리
    const [isBlinking, setIsBlinking] = useState(false);

    useEffect(() => {
        if (props.focused) {
            setIsBlinking(true);
            if (itemRef.current) {
                itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
            }
            // 3초 후 깜빡임 해제
            const timer = setTimeout(() => setIsBlinking(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [props.focused]);

    // 새창으로 PDF 열기
    const openPDFReview = (page) => {
        const review_window = window.open(
            "/reference/" + props.reference.uuid + "/review/" + props.reference.file_uuid,
        );
        const channel = Constants.REVIEW_MESSAGE_CHANNEL;
        const payload = {
            page: page,
            thread_id: props.message.thread,
            message_id: props.message.id,
        };

        // 자식 윈도우와 handshaking 후 메시지 전송
        const readyListener = (event) => {
            if (
                event.source === review_window &&
                event.origin === window.location.origin &&
                event.data?.__channel === channel
            ) {
                review_window.postMessage(
                    {
                        __channel: channel,
                        payload: payload,
                    },
                    window.location.origin,
                );
                window.removeEventListener("message", readyListener);
            }
        };
        window.addEventListener("message", readyListener);
    };

    if (!props.message) {
        return <NoMessageContainer />;
    }

    // region: 삭제된 메시지 랜더링
    if (props.message.is_deleted) {
        if (props.showDeletedMessage) {
            return (
                <MessageContainer bgColor={props.bgColor}>
                    <ProfileIcon>
                        <Icon name={"userFill"} color={"var(--color-SubBlack)"} />
                    </ProfileIcon>
                    <GeneralText size={"xsmall"} margin={"0 4px 0 0"} color={"var(--color-Grey1)"}>
                        {parseDate(props.message.updated_at) + " " + parseTime(props.message.updated_at)}
                    </GeneralText>
                    <DeletedView>
                        <GeneralText size={"small"} color={"var(--color-Grey1)"}>
                            삭제되었습니다.
                        </GeneralText>
                    </DeletedView>

                    {/* 추가 메시지 버튼으로 스레드 진입 */}
                    {props.isThread && props.thread.messages.length - 1 > 0 && (
                        <ThreadEnterText
                            onClick={() => {
                                props.enterThreadDetailView(props.thread);
                            }}
                        >
                            {props.thread.messages.length - 1}개의 추가 메시지
                        </ThreadEnterText>
                    )}
                </MessageContainer>
            );
        } else {
            return <></>;
        }
    }

    // region: 스레드 랜더링
    if (props.isThread) {
        return (
            <ThreadContainer
                bgColor={props.bgColor}
                onClick={() => {
                    props.enterThreadDetailView(props.thread);
                }}
            >
                <ThreadText
                    dangerouslySetInnerHTML={{
                        __html: props.message.content.replace(
                            /@\[([^\]]+)\]\(([^)]+)\)/g,
                            `<span style="color: green; font-size: 1.4rem; display: inline-block;">@$1</span>`,
                        ),
                    }}
                />
                <ThreadFooter>
                    <ThreadDateText>
                        {parseDate(props.message.updated_at) + " " + parseTime(props.message.updated_at)}
                    </ThreadDateText>

                    {/* 추가 메시지 버튼으로 스레드 진입 */}
                    {props.isThread && props.thread.messages.filter((x) => x.is_deleted === false).length - 1 > 0 && (
                        <ThreadEnterText
                            onClick={() => {
                                props.enterThreadDetailView(props.thread);
                            }}
                        >
                            {props.thread.messages.filter((x) => x.is_deleted === false).length - 1}
                            개의 추가 메시지
                        </ThreadEnterText>
                    )}
                </ThreadFooter>
            </ThreadContainer>
        );
    }

    // region: 메시지 랜더링
    return (
        <MessageContainer ref={itemRef} isBlinking={isBlinking}>
            <MessageSection>
                <MessageHeader>
                    {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                        <ProfileIcon>
                            {(() => {
                                const profile = (props.profiles || []).find((p) => p.id === props.message.profile);
                                return profile?.image_url ? (
                                    <img
                                        src={
                                            process.env.REACT_APP_ENV === "local"
                                                ? `http://${process.env.REACT_APP_API_BASE_URL}${profile.image_url}`
                                                : profile.image_url
                                        }
                                        alt={profile.name}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                        }}
                                    />
                                ) : (
                                    <Icon name={"userFill"} color={"var(--color-SubBlack)"} />
                                );
                            })()}
                        </ProfileIcon>
                    )}
                    {props.message.role === Constants.REVIEW_ROLE_AI && (
                        <ProfileIcon>
                            <Icon name={"aiResponse"} size={"12px"} color={"var(--color-Key)"} />
                        </ProfileIcon>
                    )}
                    {props.message.role === Constants.REVIEW_ROLE_HUMAN && (
                        <ProfileText>
                            {(() => {
                                const profile = (props.profiles || []).find((p) => p.id === props.message.profile);
                                return profile ? `@${profile.name}` : "";
                            })()}
                        </ProfileText>
                    )}
                    {props.message.role === Constants.REVIEW_ROLE_AI && <ProfileText>@PREP</ProfileText>}
                    <MessageDateText>
                        {parseDate(props.message.updated_at) + " " + parseTime(props.message.updated_at)}
                    </MessageDateText>
                </MessageHeader>

                <MessageBody>
                    {props.message.review_box && props.message.review_box_image && (
                        <HighlightImage
                            color={props.message.review_box_color}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (props.setCurPage && props.setSearchBox) {
                                    // ReferenceReviewPage 에서 접근한 경우
                                    props.setCurPage(props.message.review_box.page);
                                    props.setSearchBox({ ...props.message.review_box });
                                } else {
                                    // ReferenceDetailPage 에서 접근한 경우
                                    openPDFReview(props.message.review_box.page);
                                }
                            }}
                        >
                            <HightlightPageText color={props.message.review_box_color}>
                                p.{props.message.review_box.page}
                            </HightlightPageText>
                            <img
                                src={"data:image/png;base64," + props.message.review_box_image.base64}
                                style={{
                                    width: props.message.review_box_image.width / 3,
                                    height: props.message.review_box_image.height / 3,
                                    // maxWidth: "800px",
                                    // maxHeight: "800px",
                                }}
                            />
                        </HighlightImage>
                    )}

                    <MessageText>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: props.message.content.replace(
                                    /@\[([^\]]+)\]\(([^)]+)\)/g,
                                    `<span style="color: green; font-size: 1.4rem; display: inline-block;">@$1</span>`,
                                ),
                            }}
                        />
                        {props.streamingMessageIds && props.streamingMessageIds.includes(props.message.id) && (
                            <AiCursor />
                        )}
                    </MessageText>

                    {/* 출처 */}
                    {props.message.sources && props.message.sources.length > 0 && (
                        <>
                            <DivideLine />
                            <SourceContent>
                                {props.message.sources.map((source, index) => {
                                    return (
                                        <SourceSubText
                                            key={index}
                                            onClick={(e) => {
                                                if (props.setCurPage) {
                                                    // ReferenceReviewPage 에서 접근한 경우
                                                    props.setCurPage(source.source_page);
                                                } else {
                                                    // ReferenceDetailPage 에서 접근한 경우
                                                    openPDFReview(source.source_page);
                                                }
                                            }}
                                        >
                                            <SourceNumText>p.{source.source_page}</SourceNumText>
                                            {source.source_text}
                                        </SourceSubText>
                                    );
                                })}
                            </SourceContent>
                        </>
                    )}
                </MessageBody>
            </MessageSection>
        </MessageContainer>
    );
}

export default PDFChatMessage;
